const ACCOUNTS_STORAGE_KEY = "jk_accounts";
const ACTIVE_ACCOUNT_STORAGE_KEY = "jk_active_account_id";
const LEGACY_TOKEN_STORAGE_KEY = "token";
const LEGACY_USER_STORAGE_KEY = "user";

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function getScopedStorage() {
  if (canUseSessionStorage()) return window.sessionStorage;
  if (canUseLocalStorage()) return window.localStorage;
  return null;
}

function readJson(key) {
  if (!canUseLocalStorage()) return null;
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch (error) {
    return null;
  }
}

function getLegacyAccount() {
  const scopedStorage = getScopedStorage();
  const fallbackStorage = canUseLocalStorage() ? window.localStorage : null;
  if (!scopedStorage && !fallbackStorage) return null;

  const token =
    scopedStorage?.getItem(LEGACY_TOKEN_STORAGE_KEY) ||
    fallbackStorage?.getItem(LEGACY_TOKEN_STORAGE_KEY) ||
    "";
  const rawUser =
    scopedStorage?.getItem(LEGACY_USER_STORAGE_KEY) ||
    fallbackStorage?.getItem(LEGACY_USER_STORAGE_KEY);
  if (!token || !rawUser) return null;

  try {
    const user = JSON.parse(rawUser);
    return normalizeStoredAccount({ token, user });
  } catch (error) {
    return null;
  }
}

function normalizeStoredAccount(account) {
  if (!account?.token || !account?.user) return null;

  const user = account.user;
  const id = String(user?.id || user?.email || account.id || account.token);
  const email = String(user?.email || "").trim().toLowerCase();

  return {
    id,
    token: String(account.token),
    user,
    email,
    refresh_cookie_name: String(account.refresh_cookie_name || ""),
    access_token_expires_at: account.access_token_expires_at || null,
    refresh_token_expires_at: account.refresh_token_expires_at || null,
  };
}

function writeLegacyActiveAccount(activeAccount) {
  const scopedStorage = getScopedStorage();
  if (!scopedStorage) return;

  if (!activeAccount) {
    scopedStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    scopedStorage.removeItem(LEGACY_USER_STORAGE_KEY);
    if (canUseLocalStorage()) {
      window.localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(LEGACY_USER_STORAGE_KEY);
    }
    return;
  }

  scopedStorage.setItem(LEGACY_TOKEN_STORAGE_KEY, activeAccount.token);
  scopedStorage.setItem(LEGACY_USER_STORAGE_KEY, JSON.stringify(activeAccount.user));
  if (canUseLocalStorage()) {
    window.localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_USER_STORAGE_KEY);
  }
}

export function getStoredAccounts() {
  const parsed = readJson(ACCOUNTS_STORAGE_KEY);
  const accounts = Array.isArray(parsed)
    ? parsed.map(normalizeStoredAccount).filter(Boolean)
    : [];

  if (accounts.length) return accounts;

  const legacy = getLegacyAccount();
  return legacy ? [legacy] : [];
}

export function getStoredActiveAccountId() {
  const scopedStorage = getScopedStorage();
  if (scopedStorage) {
    const scopedValue = scopedStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    if (scopedValue) return scopedValue;
  }
  if (!canUseLocalStorage()) return null;
  return window.localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY) || null;
}

export function getActiveStoredAccount(accounts = getStoredAccounts(), activeAccountId = getStoredActiveAccountId()) {
  if (!accounts.length) return null;
  return accounts.find((account) => account.id === activeAccountId) || accounts[0];
}

export function persistStoredAccounts(accounts, activeAccountId = null) {
  if (!canUseLocalStorage() && !canUseSessionStorage()) {
    return { accounts: [], activeAccountId: null, activeAccount: null };
  }

  const dedupedMap = new Map();
  accounts
    .map(normalizeStoredAccount)
    .filter(Boolean)
    .forEach((account) => {
      dedupedMap.set(account.id, account);
    });

  const nextAccounts = Array.from(dedupedMap.values());
  const nextActiveAccount =
    nextAccounts.find((account) => account.id === activeAccountId) || nextAccounts[0] || null;
  const nextActiveAccountId = nextActiveAccount?.id || null;

  if (nextAccounts.length && canUseLocalStorage()) {
    window.localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(nextAccounts));
  } else if (canUseLocalStorage()) {
    window.localStorage.removeItem(ACCOUNTS_STORAGE_KEY);
  }

  const scopedStorage = getScopedStorage();
  if (scopedStorage) {
    if (nextActiveAccountId) {
      scopedStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, nextActiveAccountId);
    } else {
      scopedStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    }
  }

  if (canUseLocalStorage()) {
    window.localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
  }

  writeLegacyActiveAccount(nextActiveAccount);

  return {
    accounts: nextAccounts,
    activeAccountId: nextActiveAccountId,
    activeAccount: nextActiveAccount,
  };
}

export function upsertStoredAccount(sessionData) {
  const account = normalizeStoredAccount(sessionData);
  if (!account) {
    return persistStoredAccounts(getStoredAccounts(), getStoredActiveAccountId());
  }

  const existing = getStoredAccounts().filter((item) => item.id !== account.id);
  return persistStoredAccounts([account, ...existing], account.id);
}

export function setStoredActiveAccount(accountId) {
  return persistStoredAccounts(getStoredAccounts(), accountId);
}

export function updateStoredAccountUser(accountId, nextUser) {
  const nextAccounts = getStoredAccounts().map((account) =>
    account.id === String(accountId)
      ? normalizeStoredAccount({ ...account, user: nextUser })
      : account
  );

  return persistStoredAccounts(nextAccounts, accountId);
}

export function removeStoredAccount(accountId) {
  const currentActiveId = getStoredActiveAccountId();
  const nextAccounts = getStoredAccounts().filter((account) => account.id !== String(accountId));
  const nextActiveId =
    currentActiveId === String(accountId) ? nextAccounts[0]?.id || null : currentActiveId;

  return persistStoredAccounts(nextAccounts, nextActiveId);
}

export function clearStoredAccounts() {
  return persistStoredAccounts([], null);
}
